import React from 'react';
import {
  Button,
  BUTTON_VARIANT_FILLED,
  BUTTON_VARIANT_OUTLINED,
  DialogFooter,
  DialogSub,
  Modal,
} from '@aibuild/ui';
import MaterialIcon from '../canvasToolbar/MaterialIcon';
import { suggestCopyName } from './suggestCopyName';

export interface ProcessDuplicateConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** The name of the process being duplicated -- shown in the body copy. */
  currentName: string;
  /**
   * Names already in use across the user's processes -- used to dodge
   * collisions when generating the auto-suggested copy name. Optional: when
   * omitted we just append `(copy)` and rely on server-side uniqueness.
   */
  existingNames?: readonly string[];
  /**
   * Called with the auto-generated copy name when the user confirms. The
   * caller fires the duplicate API + handles success / failure toasts;
   * we just close on a successful resolution via the `isDuplicating`
   * flag flipping back to `false`.
   */
  onSubmit: (newName: string) => void | Promise<void>;
  isDuplicating?: boolean;
  dataTestId?: string;
}

/**
 * Confirmation-style "Duplicate this process?" dialog -- mirrors the handoff
 * (ProcessSidePanel.jsx:1113-1141). NO input field: the new name is auto-
 * generated as `${currentName} (copy)` (with `(copy 2)` / `(copy 3)`
 * collision avoidance) and the user just confirms.
 *
 * Distinct from {@link ProcessDuplicateDialog} which keeps an input box for
 * the side-panel kebab where the user picks the duplicate's name. The
 * canvas toolbar's "Duplicate & edit" button only ever wants the
 * confirmation flow because the user's intent is "give me an editable
 * copy NOW", not "configure the new copy's metadata".
 */
const ProcessDuplicateConfirmDialog: React.FC<
  ProcessDuplicateConfirmDialogProps
> = ({
  open,
  onClose,
  currentName,
  existingNames = [],
  onSubmit,
  isDuplicating = false,
  dataTestId = 'process-duplicate-confirm-dialog',
}) => {
  // Compute the suggested name on every render -- cheap, and we want it to
  // refresh if the user re-opens the dialog after the existing-names list
  // has changed (e.g. somebody else added a process via real-time updates).
  // Only used while the dialog is open; closed renders bail out below.
  const suggested = open ? suggestCopyName(currentName, existingNames) : '';

  const handleConfirm = () => {
    if (isDuplicating) return;
    Promise.resolve(onSubmit(suggested)).catch(() => undefined);
  };

  return (
    <Modal
      open={open}
      onClose={isDuplicating ? () => undefined : onClose}
      title="Duplicate this process?"
      // Design uses `max-width: 560px` for confirmation modals
      // (`.wf-details` in styles.css:7251); 'md' in our Modal scale is
      // also 560px so the rendered width matches the handoff. 'sm'
      // (420px) was too narrow and bumped the body to ~6 lines.
      size="md"
      dataTestId={dataTestId}
      hideCloseButton={isDuplicating}
      disableEscape={isDuplicating}
      disableBackdropClose={isDuplicating}
      footer={
        <DialogFooter>
          <Button
            variant={BUTTON_VARIANT_OUTLINED}
            onClick={onClose}
            disabled={isDuplicating}
            dataTestId={`${dataTestId}__cancel`}
          >
            Cancel
          </Button>
          <Button
            variant={BUTTON_VARIANT_FILLED}
            leadingIcon={<MaterialIcon name="content_copy" />}
            onClick={handleConfirm}
            loading={isDuplicating}
            dataTestId={`${dataTestId}__submit`}
          >
            Duplicate &amp; edit
          </Button>
        </DialogFooter>
      }
    >
      <DialogSub data-testid={`${dataTestId}__copy`}>
        Published processes are read-only so every run is reproducible. To
        make changes, we&rsquo;ll create a draft copy named{' '}
        <strong data-testid={`${dataTestId}__suggested-name`}>{suggested}</strong>{' '}
        you can edit freely. The original stays published.
      </DialogSub>
    </Modal>
  );
};

export default ProcessDuplicateConfirmDialog;
